ObjectUtils.define('EventDelegater', {
  FORWARD: 'forward',
  STOP: 'stop',
  enableDebug: 0,
  _pageResizeTimeout: null,

  getTarget: function getTarget(event) {
    return DOM.findById(event.target);
  },

  observe: function observe(elem, eventName, observer) {
    DOM.findById(elem).on(eventName, observer);
  },

  stop: function stop(event) {
    event.preventDefault();
    event.stopPropagation();
  },

  setup: function setup() {
    this.enableDebug && Logger.log(this, 'Setting up');

    var jWindow = DOM.findBySelector(window);
    this.observe(document, 'click', FunctionUtils.bind(this, this._delegateClick));

    // https://stackoverflow.com/questions/17384218/jquery-input-event
    this.observe(document, 'input propertychange keyup keydown', FunctionUtils.bind(this, this._delegateKeyboardEvent));

    // jQuery specific events. Not defined by W3C standards.
    this.observe(document, 'focusin', FunctionUtils.bind(this, this._delegateFocusIn));
    this.observe(document, 'focusout', FunctionUtils.bind(this, this._delegateBlur));
    jWindow.on('resize', FunctionUtils.bind(this, this._pageDidResize));

    // This only fires on input blur (at least in Chrome)
    // this.observe(document, 'change', FunctionUtils.bind(this, this._delegateChange));
  },
  
  _delegateClick: function _delegateClick(event) {
    // Ignore right clicks. Needed for Firefox.
    if (event.which === 3) {
      return;
    }

    var target = EventDelegater.getTarget(event);

    this.enableDebug && Logger.log(this, 'Delegating click event', {
      event: event,
      target: target,
    });

    NotificationManager.trigger(EventType.CLICK, {
      target: target
    });

    // Only do this when processing the actual target node.
    // Always allow clicks to propagate up. Some controllers only handle clicks and not
    // input changes (e.g. UIDropdownController in multi-select mode)
    this._maybeDelegateClickToInputs(target);

    DOM.maybeFindParent(target, FunctionUtils.bind(this, function(node) {
      try {
        this.enableDebug && Logger.log(this, 'Trying node for click', {
          isSubmitButton: DOM.isSubmitButton(node),
          elem: node[0],
          target: target[0],
        });

        if (DOM.getAttribute(node, 'autodelegate') === 'false') {
          return;
        }

        if (this._maybeDelegateClickToElement(node, target, event)) {
          EventDelegater.stop(event);
          return true;
        }
      } catch (e) {
        EventDelegater.stop(event);
        ErrorManager.logException(e);
        return true;
      }
    }));
  },

  _maybeDelegateClickToElement(node, target, event) {
    var eventData = {
      event: event
    };

    if (this._maybeDelegateToController('didClick', node, target, eventData) !== this.FORWARD) {
      return true;
    }

    if (this._maybeDelegateClickToLink(node, target, eventData) !== this.FORWARD) {
      return true;
    }

    if (this._maybeDelegateClickToForm(node, target) !== this.FORWARD) {
      return true;
    }

    if (DOM.hasClass(target, 'toggleLinkContainer')) {
      var toggleParentClass = DOM.getAttribute(target, 'toggleParentClass');
      var toggleClass = DOM.getAttribute(target, 'toggleClass');

      var parent = DOM.findParentByClass(target, toggleParentClass);
      DOM.toggleClass(parent, toggleClass);

      return true;
    }

    return false;
  },

  // Chrome:
  // - Keypress not fired: arrow, backspace
  // - Keyup always fired
  // - Keydown always fired
  // Firefox:
  // - Keypress not fired: shift, function, ctrl, etc.
  // - Keyup always fired
  // - Keydown always fired
  // IE:
  // - Keypress not fired: arrow, backspace
  // - Keyup always fired
  // - Keydown always fired
  // Keydown advantages:
  // - lets cancel default behavior for events like page scroll on hitting up/down arrows, enter to submit form, etc.
  // Keyup advantages:
  // - text field value can be read easily
  _delegateKeyboardEvent: function _delegateKeyboardEvent(event) {
    var eventData = KeyboardUtils.maybeGetEventData(event);

    if (!eventData) {
      return;
    }

    var target = eventData.target;

    if (eventData.didValueChange) {
      if (this.delegateViaController('didControlChangeValue', target, eventData) === EventDelegater.STOP) {
        EventDelegater.stop(event);
        return;
      }
    }

    if (this.delegateViaController('didKeypress', target, eventData) === EventDelegater.STOP) {
      EventDelegater.stop(event);
      return;
    }

    if (eventData.shouldSubmitForm) {
      var form = DOM.maybeFindParentByTag(target, 'form');
      if (!form) {
        return;
      }

      var submitStatus = UIFormController.submit(form, target);

      if (submitStatus === EventDelegater.STOP) {
        EventDelegater.stop(event);
      }

      return;
    }

    if (eventData.isEscapeKey) {
      // Clear this first, so subsequent actions can still be logged.
      if (Runtime.isDev()) {
        console.clear();
      }
      NotificationManager.trigger(EventType.KEYPRESS_ESCAPE);
      DOM.blur(DOM.getActiveElement());
    }
  },

  _delegateFocusIn: function _delegateFocusIn(event) {
    var target = DOM.findById(event.target);

    if (!DOM.isTextField(target)) {
      return;
    }

    var searchType = DOM.getAttribute(target, 'searchType');
    if (searchType) {
      AutoCompleter.installByType(target, searchType);
    }

    this.delegateViaController('didFocus', target, {
      event: event
    });
  },

  _delegateBlur: function _delegateBlur(event) {
    var target = DOM.findById(event.target);
    this.enableDebug && Logger.log(this, 'Delegating blur event', {
      target: target,
    });
    this.delegateViaController('didBlur', target);

    if (DOM.isAmountInput(target)) {
      this._didBlurAmountInput(target);
    }
  },

  delegateViaController: function delegateViaController(eventName, target, eventData) {
    this.enableDebug && Logger.log(this, 'Delegating event via controller', {
      event: eventName,
      target: target,
    });
    Assert.TRUTHY(target, '[EventDelegater] Expected non-empty element for delegated event ['+eventName+']');

    var returnValue = EventDelegater.FORWARD;
    DOM.maybeFindParent(target, FunctionUtils.bind(this, function(node) {
      this.enableDebug && Logger.log(this, 'Trying node for event', {
        event: eventName,
        elem: node[0],
      });
      if (this._maybeDelegateToController(eventName, node, target, eventData) === this.STOP) {
        returnValue = EventDelegater.STOP;
        return false;
      }
    }));
    return returnValue;
  },

  _maybeDelegateToController: function _maybeDelegateToController(eventName, container, target, eventData) {
    var controller = UIController.maybeGet(container) || {};
    if (!controller[eventName]) {
      return this.FORWARD;
    }
    var result = controller[eventName](container, target, eventData);
    this.enableDebug && Logger.log(this, 'Delegated event to controller', {
      event: eventName,
      controller: DOM.getAttribute(container, 'controller'),
      result: result,
      elem: container[0],
    });
    return result;
  },

  _maybeDelegateClickToInputs: function _maybeDelegateClickToInputs(target) {
    if (DOM.tagName(target) !== 'input') {
      return;
    }

    var radioContainer = DOM.maybeFindParentByClass(target, 'labelledRadioContainer');
    if (radioContainer) {
      this.delegateViaController('didControlChangeValue', target, {
        name: DOM.getAttribute(target, 'name'),
        value: DOM.value(target),
      });
      return;
    }

    if (DOM.isRegularCheckbox(target)) {
      var checkbox = target;
      this.enableDebug && Logger.log(this, 'Target is checkbox', target);

      if (UIControlController.shouldAutoSubmit(checkbox)) {
        UIFormController.submitForControl(checkbox);
      } else {
        this.delegateViaController('didControlChangeValue', target, {
          name: DOM.getAttribute(checkbox, 'name'),
          isSelected: DOM.isCheckboxSelected(checkbox),
        });
      }

      return;
    }
  },

  _maybeDelegateClickToLink: function _maybeDelegateClickToLink(container, target, eventData) {
    // Logged out pages don't have UILinkController loaded, so do the check here.
    if (!Runtime.areLinksAsync) {
      return EventDelegater.FORWARD;
    }

    if (!UILinkController.canAutoDelegate(container)) {
      return EventDelegater.FORWARD;
    }

    return UILinkController.didClick(container, target, eventData);
  },

  _maybeDelegateClickToForm: function _maybeDelegateClickToForm(node, target) {
    if (!DOM.isSubmitButton(node)) {
      return EventDelegater.FORWARD;
    }

    // Ignore this click. Sometimes the button has a href of the type '#'.
    // Do not forward the event, as the default browser behavior can cause weird stuff to happen.
    if (
      DOM.hasClass(node, 'disabled')
      || DOM.hasClass(node, 'interactionDisabled')
      || DOM.hasClass(node, 'loading')
    ) {
      return EventDelegater.STOP;
    }

    var form = DOM.findParentByTag(node, 'form');
    this.enableDebug && Logger.log(this, 'Submitting form', form);
    return UIFormController.submit(form, node);
  },

  _pageDidResize: function _pageDidResize(event) {
    var target = EventDelegater.getTarget(event);
    var isWindowTarget = target.is(DOM.findBySelector(window));

    if (!isWindowTarget) {
      return;
    }

    if (this._pageResizeTimeout) {
      clearTimeout(this._pageResizeTimeout);
      this._pageResizeTimeout = null;
    }

    // Try to batch these events
    this._pageResizeTimeout = setTimeout(function() {
      this.enableDebug && Logger.log(this, 'Triggering resize event', {
        target: target,
        event: event,
      });

      NotificationManager.trigger(EventType.PAGE_DID_RESIZE, {
        event: event
      });
    }, 300);
  },

  _scrollHandlers: [],
  _scrollListener: null,
  registerScrollHandler: function registerScrollHandler(handler) {
    this._scrollHandlers.push(handler);

    if (this._scrollListener === null) {
      var jWindow = DOM.findBySelector(window);
      this._scrollListener = jWindow.on('scroll', FunctionUtils.bind(this, function(event) {
        var target = EventDelegater.getTarget(event);
        if (!target.is(DOM.findBySelector(document))) {
          return;
        }

        ArrayUtils.each(this._scrollHandlers, function(index, handler) {
          handler({
            scrollTop: DOM.getPageScrollOffset(),
          });
        });
      }));
    }
  },

  _didBlurAmountInput: function _didBlurAmountInput(elem) {
    var value = DOM.value(elem);
    var computedValue = MathUtils.maybeEvaluateExpression(value);
    if (computedValue !== null) {
      var formattedValue = MathUtils.formatAmount(computedValue);
      DOM.setValue(elem, formattedValue);
    }
  }
});

Vendor.jQuery(document).ready(function onDocumentReady() {
  EventDelegater.setup();
});
